import Service, { service } from '@ember/service';
import Options from '@linc-technologies/linced-common/enums/options';
import ProgressionJudgement from '@linc-technologies/linced-common/enums/progression-judgement';
import type OptionsService from '@linc-technologies/linced-common/services/options';
import type { OptionsSchool } from '@linc-technologies/linced-common/services/options';
import {
  Dark,
  Light,
  isLight,
} from '@linc-technologies/linced-common/utils/colour';

const codeMale = 'M';
const codeFemale = 'F';

// GENDER_OPTIONS gives the school option of each gender code. Other codes
// use the neutral colour.
const GENDER_OPTIONS = new Map([
  [codeMale, 'core:theme:genderColoursMale'],
  [codeFemale, 'core:theme:genderColourFemale'],
]);

// PROGRESS_OPTIONS gives the school option of each progression judgement.
const PROGRESS_OPTIONS = new Map<number, string>([
  [ProgressionJudgement.WELL_BELOW, 'progress:colours:first'],
  [ProgressionJudgement.BELOW, 'progress:colours:second'],
  [ProgressionJudgement.AT, 'progress:colours:third'],
  [ProgressionJudgement.ABOVE, 'progress:colours:fourth'],
  [ProgressionJudgement.WELL_ABOVE, 'progress:colours:fifth'],
]);

// ColoursService gives the gender and progress colours of a school.
export default class ColoursService extends Service {
  @service
  declare options: OptionsService;

  // gender gives the colour of the gender code, as a hex string.
  gender(school: OptionsSchool, genderCode: string): string | undefined {
    const option =
      GENDER_OPTIONS.get(genderCode) ?? 'core:theme:genderColourNeutral';
    return this.options.schoolGet(school, option) as string | undefined;
  }

  // genderMale returns a hex colour string based on the set school option for
  // the male gender.
  genderMale(school: OptionsSchool): string | undefined {
    return this.gender(school, codeMale);
  }

  // genderFemale returns a hex colour string based on the set school option
  // for the female gender.
  genderFemale(school: OptionsSchool): string | undefined {
    return this.gender(school, codeFemale);
  }

  // genderNeutral returns a hex colour string based on the set school option
  // for the neutral gender.
  genderNeutral(school: OptionsSchool): string | undefined {
    return this.gender(school, '');
  }

  // progress gives the colour of the progression judgement (1 to 5), as a
  // hex string.
  progress(school: OptionsSchool, judgement: number): string | undefined {
    const option = PROGRESS_OPTIONS.get(judgement);
    return option
      ? (this.options.schoolGet(school, option) as string | undefined)
      : undefined;
  }

  // calculateTextColour gives black or white text, by the perceived
  // brightness of colour.
  calculateTextColour(colour: string | null | undefined): string {
    return colour && !isLight(colour) ? Light : Dark;
  }

  // charts gives the progress colours that D3 can use as a theme.
  charts(school: OptionsSchool): (string | undefined)[] {
    const below = this.progress(school, ProgressionJudgement.BELOW);
    const at = this.progress(school, ProgressionJudgement.AT);
    const above = this.progress(school, ProgressionJudgement.ABOVE);

    if (this.options.schoolGet(school, Options.CHARTS_SHOW_ALL_COLOURS)) {
      return [
        this.progress(school, ProgressionJudgement.WELL_BELOW),
        below,
        at,
        above,
        this.progress(school, ProgressionJudgement.WELL_ABOVE),
      ];
    }

    // Not showing all colours; use the original 4-colour version.
    return [below, at, above, 'transparent'];
  }
}
